import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { repoCaseService } from "../../../services/repoCase.service";
import useWorkMode from "../../../hooks/useWorkMode";
import useOfflineSync from "../../../hooks/useOfflineSync";
import { searchOfflineVehicles } from "../../../offline/offlineSearch";
import confirmationService from "../../../services/confirmation.service";
import VehicleDetailModal from "./VehicleDetailModal";
import VehiclePreviewSheet from "./VehiclePreviewSheet";
import { shouldShowAdminOnlyCaseFields } from "../../../utils/caseFieldVisibility";
import {
  buildNotifyBankApiPayload,
  buildNotifyContextFromCase,
  openBankNotifyShare,
  openTraceReportShare,
} from "../../../utils/bankNotifyShare";
import { emitDashboardRefresh } from "../../../utils/dashboardEvents";
import { normalizeVehicleNumber } from "../../../utils/vehicleNumberUtils";
import {
  getItems,
  isMongoCaseId,
  resolveActiveSearch,
  VEHICLE_SUFFIX_LENGTH,
  CHASSIS_SUFFIX_LENGTH,
  sanitizeVehicleSuffixInput,
  sanitizeChassisSuffixInput,
  getFindVehiclesValidationMessage,
  sortFindVehicleItems,
  uniqueFindVehicleItems,
  splitResultsByColumn,
} from "./findVehiclesHelpers";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { captureCurrentGps } from "../../../utils/captureCurrentGps";
import VehicleChassisSearchBar from "./VehicleChassisSearchBar";
import FindVehiclesStatsPanel from "../../../components/findVehicles/FindVehiclesStatsPanel";
import "../../../styles/vehicles.css";

/** Infinite scroll page size — must match backend FIND_VEHICLES_PAGE_SIZE. */
const PAGE_SIZE = 100;

/** Keep URL in sync (bookmarks / share) without delaying results. */
const URL_SYNC_DEBOUNCE_MS = 400;

/** Delay before hitting the API while the user is still typing. */
const FETCH_DEBOUNCE_MS = 250;

function appendUniqueById(existing, incoming) {
  if (!incoming.length) return existing;

  const seen = new Set(
    existing.map((item) => String(item._id || item.id || ""))
  );

  const extra = [];

  for (const item of incoming) {
    const id = String(item._id || item.id || "");

    if (!id || seen.has(id)) continue;

    seen.add(id);
    extra.push(item);
  }

  return extra.length ? existing.concat(extra) : existing;
}

function hasMorePages(res, pageItems, loadedCount, total) {
  if (
    res?.hasNext === true ||
    res?.pagination?.hasNextPage === true
  ) {
    return true;
  }

  if (
    res?.hasNext === false ||
    res?.pagination?.hasNextPage === false
  ) {
    return false;
  }

  if (pageItems.length < PAGE_SIZE) return false;

  if (Number.isFinite(total) && total > 0) {
    return loadedCount < total;
  }

  return pageItems.length === PAGE_SIZE;
}

function cardPrimaryLabel(item, mode) {
  if (mode === "chassis") {
    const chassis = String(item.chassisNumber || "").trim();
    return chassis || "—";
  }

  const vn = item.vehicleNumber;

  if (!vn) return "—";

  return normalizeVehicleNumber(vn);
}

export default function FindVehiclesResults() {
  const { auth } = useAuth();
  const { isOffline } = useWorkMode();
  const offlineSync = useOfflineSync();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawQ = searchParams.get("q") || "";

  const typeParam = (
    searchParams.get("type") || "vehicle"
  ).toLowerCase();

  const searchMode =
    typeParam === "chassis" ? "chassis" : "vehicle";

  const query =
    searchMode === "chassis"
      ? sanitizeChassisSuffixInput(rawQ)
      : sanitizeVehicleSuffixInput(rawQ);

  const initialQ = searchParams.get("q") || "";

  const initialType = (
    searchParams.get("type") || "vehicle"
  ).toLowerCase();

  const [draftVehicle, setDraftVehicle] = useState(() =>
    initialType === "chassis"
      ? ""
      : sanitizeVehicleSuffixInput(initialQ)
  );

  const [draftChassis, setDraftChassis] = useState(() =>
    initialType === "chassis"
      ? sanitizeChassisSuffixInput(initialQ)
      : ""
  );

  const [lastInteraction, setLastInteraction] = useState(() =>
    initialType === "chassis" ? "chassis" : "vehicle"
  );

  const [results, setResults] = useState([]);
  const [totalFromApi, setTotalFromApi] = useState(0);
  const [totalIsApproximate, setTotalIsApproximate] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [heldSearch, setHeldSearch] = useState(null);
  const [previewVehicle, setPreviewVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  const isAdminPreview = shouldShowAdminOnlyCaseFields(
    auth?.user?.role
  );

  const loadMoreRef = useRef(null);
  const searchGenRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const loadingSearchRef = useRef(false);
  const resultsRef = useRef([]);
  const inputHydrateLockRef = useRef({
    vehicle: false,
    chassis: false,
  });

  const reporter = useMemo(
    () => ({
      name: auth?.user?.name || "",
      role: auth?.user?.role || "",
      phone: auth?.user?.phone || "",
    }),
    [
      auth?.user?.name,
      auth?.user?.role,
      auth?.user?.phone,
    ]
  );

  useEffect(() => {
    if (searchMode === "chassis") {
      if (
        rawQ &&
        !inputHydrateLockRef.current.chassis
      ) {
        setDraftChassis(
          sanitizeChassisSuffixInput(rawQ)
        );
      }
    } else if (
      rawQ &&
      !inputHydrateLockRef.current.vehicle
    ) {
      setDraftVehicle(
        sanitizeVehicleSuffixInput(rawQ)
      );
    }
  }, [rawQ, searchMode]);

  const liveResolved = useMemo(
    () =>
      resolveActiveSearch(
        draftVehicle,
        draftChassis,
        lastInteraction
      ),
    [
      draftVehicle,
      draftChassis,
      lastInteraction,
    ]
  );

  const validationMessage = useMemo(
    () =>
      liveResolved
        ? ""
        : getFindVehiclesValidationMessage(
            draftVehicle,
            draftChassis,
            lastInteraction
          ),
    [
      liveResolved,
      draftVehicle,
      draftChassis,
      lastInteraction,
    ]
  );

  const fetchFromUrlFallback =
    !liveResolved &&
    !validationMessage &&
    (
      (
        searchMode === "chassis" &&
        query.length === CHASSIS_SUFFIX_LENGTH
      ) ||
      (
        searchMode === "vehicle" &&
        query.length === VEHICLE_SUFFIX_LENGTH
      )
    );

  const fetchQuery = useMemo(() => {
    if (liveResolved) {
      return liveResolved.normalized;
    }

    if (fetchFromUrlFallback) {
      return query;
    }

    if (
      !draftVehicle &&
      !draftChassis &&
      heldSearch?.query
    ) {
      return heldSearch.query;
    }

    return "";
  }, [
    liveResolved,
    fetchFromUrlFallback,
    query,
    draftVehicle,
    draftChassis,
    heldSearch,
  ]);

  const fetchMode = useMemo(() => {
    if (liveResolved) {
      return liveResolved.mode;
    }

    if (fetchFromUrlFallback) {
      return searchMode;
    }

    if (
      !draftVehicle &&
      !draftChassis &&
      heldSearch?.mode
    ) {
      return heldSearch.mode;
    }

    return "vehicle";
  }, [
    liveResolved,
    fetchFromUrlFallback,
    searchMode,
    draftVehicle,
    draftChassis,
    heldSearch,
  ]);

  const shouldSearch = Boolean(fetchQuery);

  const runSearchPage = useCallback(
    async (page, { signal } = {}) => {
      if (isOffline) {
        return searchOfflineVehicles({
          dbName: offlineSync.dbName,
          query: fetchQuery,
          mode: fetchMode,
          page,
          limit: PAGE_SIZE,
        });
      }

      const apiType =
        fetchMode === "chassis"
          ? "chassisNumber"
          : "vehicleNumber";

      return repoCaseService.getCases(
        auth.token,
        {
          search: fetchQuery,
          type: apiType,
          page,
          limit: PAGE_SIZE,
          signal,
        }
      );
    },
    [
      auth?.token,
      fetchQuery,
      fetchMode,
      isOffline,
      offlineSync.dbName,
    ]
  );

  useEffect(() => {
    const resolved = resolveActiveSearch(
      draftVehicle,
      draftChassis,
      lastInteraction
    );

    const timer = setTimeout(() => {
      if (!resolved) {
        if (heldSearch) return;

        if (rawQ) {
          navigate(
            "/find-vehicles/results",
            { replace: true }
          );
        }

        return;
      }

      const next =
        `/find-vehicles/results?q=${encodeURIComponent(
          resolved.normalized
        )}&type=${resolved.mode}`;

      const current =
        `${window.location.pathname}${window.location.search}`;

      if (current !== next) {
        navigate(next, { replace: true });
      }
    }, URL_SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    draftVehicle,
    draftChassis,
    lastInteraction,
    navigate,
    rawQ,
    heldSearch,
  ]);

  useEffect(() => {
    if (!auth?.token || isOffline) return;

    repoCaseService
      .warmSearchCache(auth.token)
      .catch(() => {});
  }, [auth?.token, isOffline]);

  useEffect(() => {
    if ((!auth?.token && !isOffline) || !shouldSearch) {
      searchGenRef.current += 1;
      pageRef.current = 1;
      hasMoreRef.current = false;
      loadingMoreRef.current = false;
      loadingSearchRef.current = false;

      setResults([]);
      setTotalFromApi(0);
      setTotalIsApproximate(false);
      setHasMore(false);
      setLoadingMore(false);
      setSearchError("");
      setLoadingSearch(false);

      resultsRef.current = [];

      return undefined;
    }

    const gen = ++searchGenRef.current;

    pageRef.current = 1;
    hasMoreRef.current = false;
    loadingMoreRef.current = false;
    loadingSearchRef.current = true;

    setLoadingSearch(true);
    setLoadingMore(false);
    setHasMore(false);
    setResults([]);
    setTotalFromApi(0);
    setTotalIsApproximate(false);

    resultsRef.current = [];

    const controller = new AbortController();
    let cancelled = false;

    const timer = setTimeout(
      async () => {
        setSearchError("");

        try {
          let res;

          try {
            res = await runSearchPage(
              1,
              {
                signal: controller.signal,
              }
            );
          } catch (firstErr) {
            if (
              firstErr?.code === "ERR_CANCELED" ||
              controller.signal.aborted
            ) {
              return;
            }

            await new Promise((resolve) =>
              setTimeout(resolve, 2500)
            );

            if (
              cancelled ||
              controller.signal.aborted ||
              gen !== searchGenRef.current
            ) {
              return;
            }

            try {
              res = await runSearchPage(
                1,
                {
                  signal: controller.signal,
                }
              );
            } catch (err) {
              throw firstErr?.response
                ? firstErr
                : err;
            }
          }

          if (
            cancelled ||
            controller.signal.aborted ||
            gen !== searchGenRef.current
          ) {
            return;
          }

          if (res?.missingLocal) {
            pageRef.current = 1;
            hasMoreRef.current = false;
            resultsRef.current = [];

            setResults([]);
            setTotalFromApi(0);
            setTotalIsApproximate(false);
            setHasMore(false);
            setSearchError(
              "Vehicle records are not stored on this device yet. Wait for sync or retry Offline."
            );

            return;
          }

          if (res?.searchTimeout) {
            pageRef.current = 1;
            hasMoreRef.current = false;
            resultsRef.current = [];

            setResults([]);
            setTotalFromApi(0);
            setTotalIsApproximate(false);
            setHasMore(false);
            setSearchError(
              "Search timed out. Try again."
            );

            return;
          }

          const pageItems = appendUniqueById(
            [],
            getItems(res)
          );

          const apiTotal = Number(
            res?.total ??
              res?.pagination?.total
          );

          const total =
            Number.isFinite(apiTotal) &&
            apiTotal >= 0
              ? apiTotal
              : pageItems.length;

          const nextHasMore = hasMorePages(
            res,
            pageItems,
            pageItems.length,
            total
          );

          pageRef.current = 1;
          hasMoreRef.current = nextHasMore;
          resultsRef.current = pageItems;

          setResults(pageItems);
          setTotalFromApi(total);
          setTotalIsApproximate(
            Boolean(res?.totalIsApproximate) &&
              nextHasMore
          );
          setHasMore(nextHasMore);

          if (isOffline && !pageItems.length) {
            setSearchError(
              "This record is not present in the local database."
            );
          }

          if (
            fetchMode === "chassis" ||
            fetchMode === "vehicle"
          ) {
            inputHydrateLockRef.current[
              fetchMode
            ] = true;

            setHeldSearch({
              query: fetchQuery,
              mode: fetchMode,
            });

            if (fetchMode === "chassis") {
              setDraftChassis("");
            } else {
              setDraftVehicle("");
            }
          }
        } catch (err) {
          if (
            cancelled ||
            err?.code === "ERR_CANCELED" ||
            controller.signal.aborted
          ) {
            return;
          }

          if (
            gen !== searchGenRef.current
          ) {
            return;
          }

          pageRef.current = 1;
          hasMoreRef.current = false;
          resultsRef.current = [];

          setResults([]);
          setTotalFromApi(0);
          setTotalIsApproximate(false);
          setHasMore(false);

          setSearchError(
            getApiErrorMessage(
              err,
              "Search failed. Try again."
            )
          );
        } finally {
          if (
            !cancelled &&
            !controller.signal.aborted &&
            gen === searchGenRef.current
          ) {
            loadingSearchRef.current = false;
            setLoadingSearch(false);
          }
        }
      },
      FETCH_DEBOUNCE_MS
    );

    return () => {
      cancelled = true;
      loadingSearchRef.current = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [
    auth?.token,
    shouldSearch,
    fetchQuery,
    fetchMode,
    runSearchPage,
    isOffline,
  ]);

  const loadNextPage = useCallback(
    async () => {
      if (
        !auth?.token ||
        !shouldSearch
      ) {
        return;
      }

      if (
        loadingSearchRef.current ||
        loadingMoreRef.current ||
        !hasMoreRef.current
      ) {
        return;
      }

      const gen = searchGenRef.current;
      const nextPage =
        pageRef.current + 1;

      loadingMoreRef.current = true;
      setLoadingMore(true);

      try {
        const res =
          await runSearchPage(nextPage);

        if (
          gen !== searchGenRef.current
        ) {
          return;
        }

        if (res?.searchTimeout) {
          hasMoreRef.current = false;
          setHasMore(false);
          return;
        }

        const incoming =
          appendUniqueById(
            [],
            getItems(res)
          );

        if (!incoming.length) {
          hasMoreRef.current = false;
          setHasMore(false);
          return;
        }

        const apiTotal = Number(
          res?.total ??
            res?.pagination?.total
        );

        const merged =
          appendUniqueById(
            resultsRef.current,
            incoming
          );

        if (
          merged.length ===
          resultsRef.current.length
        ) {
          hasMoreRef.current = false;
          setHasMore(false);
          return;
        }

        const total =
          Number.isFinite(apiTotal) &&
          apiTotal >= 0
            ? apiTotal
            : merged.length;

        const nextHasMore =
          hasMorePages(
            res,
            incoming,
            merged.length,
            total
          );

        resultsRef.current = merged;
        pageRef.current = nextPage;
        hasMoreRef.current =
          nextHasMore;

        setResults(merged);
        setTotalFromApi(total);
        setTotalIsApproximate(
          Boolean(
            res?.totalIsApproximate
          ) && nextHasMore
        );
        setHasMore(nextHasMore);
      } catch (err) {
        if (
          err?.code === "ERR_CANCELED" ||
          gen !== searchGenRef.current
        ) {
          return;
        }

        hasMoreRef.current = false;
        setHasMore(false);

        setSearchError(
          getApiErrorMessage(
            err,
            "Could not load more results."
          )
        );
      } finally {
        if (
          gen === searchGenRef.current
        ) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      }
    },
    [
      auth?.token,
      shouldSearch,
      runSearchPage,
    ]
  );

  useEffect(() => {
    const node = loadMoreRef.current;

    if (
      !node ||
      !hasMore ||
      loadingSearch
    ) {
      return undefined;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          if (
            entries.some(
              (entry) =>
                entry.isIntersecting
            )
          ) {
            loadNextPage();
          }
        },
        {
          root: null,
          rootMargin: "240px 0px",
          threshold: 0,
        }
      );

    observer.observe(node);

    return () =>
      observer.disconnect();
  }, [
    hasMore,
    loadingSearch,
    loadNextPage,
    results.length,
  ]);

  /**
   * Share trace / notification.
   *
   * whatsappPhones is forwarded so the backend
   * can generate a WhatsApp URL per recipient.
   */
  const handleShare = async (
    channel,
    item,
    extras = {}
  ) => {
    if (
      !auth?.token ||
      shareBusy ||
      !item
    ) {
      return;
    }

    const mongoId = String(
      item._id ||
        item.id ||
        ""
    );

    const selectedPhone = String(
      extras.notifyPhone || ""
    ).trim();

    /**
     * Selected WhatsApp recipients.
     *
     * VehicleDetailModal sends whatsappPhones
     * (last 10 digits, 1–4 numbers).
     *
     * Old callers may still send whatsappPhone.
     */
    const extrasObj =
      extras &&
      typeof extras === "object" &&
      !Array.isArray(extras)
        ? extras
        : {};

    const selectedWhatsAppPhones =
      Array.isArray(
        extrasObj.whatsappPhones
      )
        ? [
            ...new Set(
              extrasObj.whatsappPhones
                .map((value) =>
                  String(value || "")
                    .replace(/\D/g, "")
                    .slice(-10)
                )
                .filter(Boolean)
            ),
          ]
        : [];

    const selectedWhatsAppPhone =
      selectedWhatsAppPhones[0] ||
      String(
        extrasObj.whatsappPhone ||
          extrasObj.notifyPhone ||
          ""
      ).trim();

    const notifyContacts =
      isAdminPreview
        ? {
            ...(selectedPhone
              ? {
                  notifyPhone:
                    selectedPhone,
                }
              : {}),
          }
        : {
            notifyPhone:
              selectedPhone ||
              item.mobileNumber ||
              item.alternateMobileNumber ||
              "",
            notifyEmail:
              item.email || "",
          };

    const notifyContext =
      buildNotifyContextFromCase(
        item,
        auth?.user
      );

    const fallbackEmail = String(
      auth?.user?.email || ""
    ).trim();

    let emailOpenedOnClick =
      false;

    const adminBankers = isAdminPreview
      ? Array.isArray(extrasObj.bankerRecipients)
        ? extrasObj.bankerRecipients.filter(
            (row) => String(row?.phone || "").trim()
          )
        : []
      : [];

    if (channel === "email") {
      if (isAdminPreview) {
        openBankNotifyShare(
          "email",
          item,
          notifyContacts,
          notifyContext,
          {
            token: auth.token,
            fromEmail: String(auth?.user?.email || "").trim(),
          }
        ).catch(() => {});
      } else {
        openTraceReportShare(
          "email",
          item,
          reporter,
          {
            requestNote: extrasObj.requestNote,
          }
        );
      }
      emailOpenedOnClick = true;
    }

    const localOnlyShare =
      isOffline || navigator.onLine === false;

    if (localOnlyShare) {
      if (channel === "email" && emailOpenedOnClick) {
        return;
      }

      setShareBusy(true);
      try {
        if (isAdminPreview) {
          if (adminBankers.length) {
            for (const banker of adminBankers) {
              const bankerContacts = {
                notifyPhone: String(banker.phone || "").trim(),
              };
              if (!bankerContacts.notifyPhone) continue;
              if (channel === "email") continue;
              await openBankNotifyShare(
                channel,
                item,
                bankerContacts,
                notifyContext,
                {}
              );
            }
          } else if (channel !== "email") {
            await openBankNotifyShare(
              channel,
              item,
              notifyContacts,
              notifyContext,
              {}
            );
          }
        } else {
          const phones = selectedWhatsAppPhones.length
            ? selectedWhatsAppPhones
            : selectedWhatsAppPhone
              ? [selectedWhatsAppPhone]
              : [];
          if (channel === "whatsapp" && phones.length) {
            phones.forEach((phone) => {
              openTraceReportShare("whatsapp", item, reporter, {
                adminPhone: phone,
                fallbackEmail,
              });
            });
          } else {
            openTraceReportShare(channel, item, reporter, {
              adminPhone: selectedWhatsAppPhone,
              fallbackEmail,
            });
          }
        }
      } catch (err) {
        window.alert(
          err?.message || "Could not open share from local vehicle data."
        );
      } finally {
        setShareBusy(false);
      }
      return;
    }

    setShareBusy(true);

    try {
      if (isAdminPreview) {
        if (adminBankers.length) {
          for (const banker of adminBankers) {
            const bankerContacts = {
              notifyPhone: String(banker.phone || "").trim(),
            };
            if (!bankerContacts.notifyPhone) continue;
            if (channel === "email") continue;
            await openBankNotifyShare(
              channel,
              item,
              bankerContacts,
              notifyContext,
              { token: auth.token }
            );
          }
        } else if (channel !== "email") {
          await openBankNotifyShare(
            channel,
            item,
            notifyContacts,
            notifyContext,
            {
              token: auth.token,
            }
          );
        }

        if (
          isMongoCaseId(mongoId)
        ) {
          repoCaseService
            .notifyBankTraced(
              mongoId,
              buildNotifyBankApiPayload(
                channel,
                auth?.user
              ),
              auth.token
            )
            .catch(() => {});
        }

        const gps = await captureCurrentGps();
        const createRes =
          await confirmationService.create(
            {
              ...(isMongoCaseId(mongoId)
                ? {
                    caseId: mongoId,
                  }
                : {}),
              searchItem: item,
              traceMode: "ONLINE",
              shareChannel: channel,
              requestNote:
                extrasObj.requestNote ||
                `Agency admin notified banker via ${channel}.`,
              ...(gps
                ? {
                    latitude: gps.latitude,
                    longitude: gps.longitude,
                    accuracy: gps.accuracy,
                  }
                : {}),
            },
            auth.token
          );

        if (createRes?.success === false) {
          throw new Error(
            createRes?.message ||
              "Could not save trace."
          );
        }

        emitDashboardRefresh();

        return createRes;
      }

      /**
       * Create confirmation.
       *
       * For WhatsApp, selected recipient phones
       * are sent in one request.
       */
      const gps = await captureCurrentGps();
      const res =
        await confirmationService.create(
          {
            ...(isMongoCaseId(mongoId)
              ? {
                  caseId: mongoId,
                }
              : {}),

            searchItem: item,

            traceMode: "ONLINE",

            shareChannel: channel,

            ...(gps
              ? {
                  latitude: gps.latitude,
                  longitude: gps.longitude,
                  accuracy: gps.accuracy,
                }
              : {}),

            ...(channel ===
              "whatsapp" &&
            selectedWhatsAppPhones.length
              ? {
                  whatsappPhones:
                    selectedWhatsAppPhones,
                }
              : channel ===
                  "whatsapp" &&
                selectedWhatsAppPhone
              ? {
                  whatsappPhone:
                    selectedWhatsAppPhone,
                }
              : channel === "sms" &&
                  selectedWhatsAppPhone
                ? {
                    notifyPhone:
                      selectedWhatsAppPhone,
                  }
                : {}),
          },
          auth.token
        );

      if (res?.success === false) {
        throw new Error(
          res?.message ||
            "Could not save trace."
        );
      }

      emitDashboardRefresh();

      if (
        channel === "email" &&
        emailOpenedOnClick
      ) {
        return;
      }

      /**
       * Backend returns WhatsApp URL(s).
       *
       * One recipient: open immediately.
       * Multiple: the modal shows per-recipient
       * buttons so popup blockers do not hide tabs.
       */
      const whatsAppUrls = Array.isArray(
        res?.traceReport?.whatsAppUrls
      )
        ? res.traceReport.whatsAppUrls.filter(
            (row) => row?.url
          )
        : [];

      const backendMessage = String(res?.traceReport?.message || "");
      const currentPlate = normalizeVehicleNumber(item.vehicleNumber);
      const messageForShare =
        currentPlate &&
        backendMessage.replace(/[\s\-_.]/g, "").toUpperCase().includes(currentPlate)
          ? backendMessage
          : undefined;

      if (channel === "whatsapp") {
        const phones = selectedWhatsAppPhones.length
          ? selectedWhatsAppPhones
          : selectedWhatsAppPhone
            ? [selectedWhatsAppPhone]
            : [];
        const fallbackPhone =
          res?.traceReport?.adminPhone || "";
        const targets = phones.length ? phones : fallbackPhone ? [fallbackPhone] : [""];
        targets.forEach((phone) => {
          openTraceReportShare("whatsapp", item, reporter, {
            message: messageForShare,
            adminPhone: phone,
            fallbackEmail,
          });
        });
      } else {
        openTraceReportShare(
          channel,
          item,
          reporter,
          {
            message: messageForShare,
            adminEmail: res?.traceReport?.adminEmail,
            adminPhone:
              selectedWhatsAppPhone ||
              res?.traceReport?.adminPhone,
            fallbackEmail,
          }
        );
      }

      return res;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        (isAdminPreview
          ? "Could not open share. Try again."
          : "Could not save trace. Admin will not see a pending confirmation.");

      window.alert(msg);
    } finally {
      setShareBusy(false);
    }
  };

  const showQuery = shouldSearch;

  const noMatches =
    showQuery &&
    !loadingSearch &&
    !searchError &&
    results.length === 0;

  const displayTitle = useMemo(() => {
    if (!showQuery) return "";

    if (fetchMode === "chassis") {
      return fetchQuery;
    }

    return fetchQuery;
  }, [
    showQuery,
    fetchQuery,
    fetchMode,
  ]);

  const {
    left: leftColumn,
    right: rightColumn,
  } = useMemo(
    () =>
      splitResultsByColumn(
        sortFindVehicleItems(
          uniqueFindVehicleItems(results, fetchMode),
          fetchMode
        )
      ),
    [results, fetchMode]
  );

  const openResult = (item) => {
    if (isAdminPreview) {
      setPreviewVehicle(item);
      setSelectedVehicle(item);
    } else {
      setSelectedVehicle(item);
    }
  };

  const renderResultCell = (
    item,
    index
  ) => (
    <button
      type="button"
      key={`${
        item._id ||
        item.id ||
        "row"
      }-${index}`}
      className="fv-excel-grid__cell"
      onClick={() =>
        openResult(item)
      }
      title="View details"
    >
      <div className="fv-excel-grid__cell-main">
        <span className="fv-excel-grid__cell-text">
          {cardPrimaryLabel(
            item,
            fetchMode
          )}
        </span>

        <span
          className="fv-excel-grid__cell-chev"
          aria-hidden
        >
          ›
        </span>
      </div>
    </button>
  );

  return (
    <div className="page find-vehicles-page fv-find-results-layout">
      {showQuery ? (
        <div className="fv-results-topbar">
          <button
            type="button"
            className="fv-results-topbar__back"
            onClick={() =>
              navigate(
                "/find-vehicles"
              )
            }
            aria-label="Back"
          >
            ←
          </button>
        </div>
      ) : null}

      <div className="fv-search-card fv-mobile-search-shell">
        <VehicleChassisSearchBar
          draftVehicle={
            draftVehicle
          }
          draftChassis={
            draftChassis
          }
          onVehicleChange={(
            value
          ) => {
            inputHydrateLockRef.current.vehicle =
              false;

            setHeldSearch(null);

            setLastInteraction(
              "vehicle"
            );

            setDraftVehicle(
              sanitizeVehicleSuffixInput(
                value
              )
            );
          }}
          onChassisChange={(
            value
          ) => {
            inputHydrateLockRef.current.chassis =
              false;

            setHeldSearch(null);

            setLastInteraction(
              "chassis"
            );

            setDraftChassis(
              sanitizeChassisSuffixInput(
                value
              )
            );
          }}
          onVehicleFocus={() =>
            setLastInteraction(
              "vehicle"
            )
          }
          onChassisFocus={() =>
            setLastInteraction(
              "chassis"
            )
          }
          onClearVehicle={() => {
            inputHydrateLockRef.current.vehicle =
              false;

            setHeldSearch(null);
            setDraftVehicle("");
          }}
          onClearChassis={() => {
            inputHydrateLockRef.current.chassis =
              false;

            setHeldSearch(null);
            setDraftChassis("");
          }}
        />
      </div>

      {!showQuery ? (
        <FindVehiclesStatsPanel />
      ) : null}

      {validationMessage && (
        <p className="fv-error fv-results-minimal-error">
          {validationMessage}
        </p>
      )}

      {searchError && (
        <p className="fv-error fv-results-minimal-error">
          {searchError}
        </p>
      )}

      <div className="fv-results-minimal-body">
        {loadingSearch &&
          results.length === 0 && (
            <div className="fv-empty-box">
              Searching…
            </div>
          )}

        {!loadingSearch &&
          showQuery &&
          noMatches && (
            <div className="fv-empty-box">
              No match for{" "}
              <strong>
                {displayTitle ||
                  fetchQuery}
              </strong>{" "}
              (
              {fetchMode ===
              "chassis"
                ? "chassis"
                : "vehicle"}
              ).
            </div>
          )}

        {showQuery &&
          results.length > 0 && (
            <>
              <div className="fv-results-total-row">
                <span className="fv-results-count">
                  Total records:{" "}
                  {totalFromApi.toLocaleString()}
                  {totalIsApproximate
                    ? "+"
                    : ""}
                </span>
              </div>

              <div
                className="fv-excel-grid"
                aria-label="Search results"
              >
                <div className="fv-excel-grid__col fv-excel-grid__col--left">
                  {leftColumn.map(
                    (
                      item,
                      index
                    ) =>
                      renderResultCell(
                        item,
                        index
                      )
                  )}
                </div>

                <div className="fv-excel-grid__col fv-excel-grid__col--right">
                  {rightColumn.map(
                    (
                      item,
                      index
                    ) =>
                      renderResultCell(
                        item,
                        leftColumn.length +
                          index
                      )
                  )}
                </div>
              </div>

              {hasMore ? (
                <div
                  ref={
                    loadMoreRef
                  }
                  className="fv-infinite-sentinel"
                  aria-hidden
                />
              ) : null}

              {loadingMore ? (
                <div
                  className="fv-infinite-loading"
                  role="status"
                >
                  Loading more…
                </div>
              ) : null}
            </>
          )}
      </div>

      {selectedVehicle && (
        <VehicleDetailModal
          item={selectedVehicle}
          overlayClassName={
            isAdminPreview &&
            previewVehicle
              ? "fv-detail-overlay--bank-strip"
              : ""
          }
          className={
            isAdminPreview &&
            previewVehicle
              ? "fv-detail-modal--bank-strip-open"
              : ""
          }
          onClose={() => {
            setSelectedVehicle(null);
            setPreviewVehicle(null);
          }}
          onShare={handleShare}
          shareBusy={shareBusy}
          onLoadedSaved={(
            vehicleItem,
            patch
          ) => {
            const id =
              vehicleItem._id ||
              vehicleItem.id;

            setSelectedVehicle(
              (prev) =>
                prev
                  ? {
                      ...prev,
                      ...patch,
                    }
                  : prev
            );

            setPreviewVehicle(
              (prev) =>
                prev &&
                (prev._id ||
                  prev.id) ===
                  id
                  ? {
                      ...prev,
                      ...patch,
                    }
                  : prev
            );

            setResults(
              (prev) => {
                const next =
                  prev.map(
                    (row) =>
                      (row._id ||
                        row.id) ===
                      id
                        ? {
                            ...row,
                            ...patch,
                          }
                        : row
                  );

                resultsRef.current =
                  next;

                return next;
              }
            );
          }}
        />
      )}

      {isAdminPreview &&
        previewVehicle && (
          <VehiclePreviewSheet
            item={
              previewVehicle
            }
            onClose={() =>
              setPreviewVehicle(
                null
              )
            }
          />
        )}
    </div>
  );
}